import React, { useState, useEffect } from "react";
import {
    Box,
    Paper,
    Typography,
    TextField,
    Button,
    Container,
    MenuItem,
    Alert
} from "@mui/material";
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import { Description, Save, Code, Visibility } from "@mui/icons-material";
import { ToggleButton, ToggleButtonGroup } from "@mui/material";
import api from '../../services/api';
import Preloader from '../../components/Preloader';

// Configuração da barra de ferramentas do Quill
const modules = {
    toolbar: [
        [{ 'font': [] }],
        [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
        ['bold', 'italic', 'underline', 'strike'],
        [{ 'color': [] }, { 'background': [] }],
        [{ 'script': 'sub' }, { 'script': 'super' }],
        ['blockquote', 'code-block'],
        [{ 'list': 'ordered' }, { 'list': 'bullet' }],
        [{ 'indent': '-1' }, { 'indent': '+1' }, { 'align': [] }],
        ['link', 'image'],
        ['clean']
    ],
};

const formats = [
    'font',
    'header',
    'bold', 'italic', 'underline', 'strike',
    'color', 'background',
    'script',
    'blockquote', 'code-block',
    'list', 'bullet',
    'indent', 'align',
    'link', 'image'
];

export default function ConfiguracaoContratos() {
    const [modelos, setModelos] = useState([]);
    const [selectedModeloId, setSelectedModeloId] = useState('');
    const [conteudo, setConteudo] = useState('');
    const [loading, setLoading] = useState(false);
    const [msg, setMsg] = useState({ type: '', text: '' });
    const [viewMode, setViewMode] = useState('visual'); // visual | source

    useEffect(() => {
        carregarModelos();
    }, []);

    const carregarModelos = async () => {
        try {
            const res = await api.get('/contrato-template');
            setModelos(res.data.dados || []);
            if (res.data.dados.length > 0) {
                // Selecionar o primeiro por padrão
                setSelectedModeloId(res.data.dados[0].id_contrato_template);
                setConteudo(res.data.dados[0].st_conteudo_html);
            }
        } catch (error) {
            console.error(error);
            setMsg({ type: 'error', text: 'Erro ao carregar modelos.' });
        }
    };

    const handleModeloChange = (e) => {
        const id = e.target.value;
        setSelectedModeloId(id);
        const modelo = modelos.find(m => m.id_contrato_template === id);
        if (modelo) {
            setConteudo(modelo.st_conteudo_html);
        }
    };

    const handleSalvar = async () => {
        setLoading(true);
        setMsg({ type: '', text: '' });
        try {
            await api.put(`/contrato-template/${selectedModeloId}`, {
                st_conteudo_html: conteudo
            });
            // Update local state to reflect saved changes
            setModelos(prev => prev.map(m => m.id_contrato_template === selectedModeloId ? { ...m, st_conteudo_html: conteudo } : m));
            setMsg({ type: 'success', text: 'Contrato atualizado com sucesso!' });
        } catch (error) {
            console.error(error);
            setMsg({ type: 'error', text: 'Erro ao salvar contrato.' });
        } finally {
            setLoading(false);
        }
    };

    const handleViewModeChange = (event, newMode) => {
        if (newMode !== null) {
            setViewMode(newMode);
        }
    };

    return (
        <Container maxWidth="lg" sx={{ p: 4 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 4, gap: 1 }}>
                <Description fontSize="large" sx={{ color: '#20ac9c' }} />
                <Typography variant="h4" component="h1">
                    Ajustar Contratos
                </Typography>
            </Box>

            {msg.text && (
                <Alert severity={msg.type} sx={{ mb: 3 }} onClose={() => setMsg({ type: '', text: '' })}>
                    {msg.text}
                </Alert>
            )}

            <Paper sx={{ p: 3 }}>
                <Box sx={{ mb: 3 }}>
                    <TextField
                        select
                        label="Selecione o Contrato"
                        value={selectedModeloId}
                        onChange={handleModeloChange}
                        fullWidth
                        disabled={modelos.length === 0}
                    >
                        {modelos.map((modelo) => (
                            <MenuItem key={modelo.id_contrato_template} value={modelo.id_contrato_template}>
                                {modelo.st_titulo} (v{modelo.nu_versao})
                            </MenuItem>
                        ))}
                    </TextField>
                </Box>

                <Box sx={{ mb: 3 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                        <Typography variant="subtitle2" sx={{ color: 'text.secondary' }}>
                            Conteúdo do Contrato
                        </Typography>
                        <ToggleButtonGroup
                            value={viewMode}
                            exclusive
                            onChange={handleViewModeChange}
                            aria-label="modo de visualização"
                            size="small"
                        >
                            <ToggleButton value="visual" aria-label="visual">
                                <Visibility fontSize="small" sx={{ mr: 1 }} /> Visual
                            </ToggleButton>
                            <ToggleButton value="source" aria-label="código fonte">
                                <Code fontSize="small" sx={{ mr: 1 }} /> HTML
                            </ToggleButton>
                        </ToggleButtonGroup>
                    </Box>

                    {viewMode === 'visual' ? (
                        <Box sx={{
                            '& .ql-container': { minHeight: '400px', fontSize: '14px' },
                            '& .ql-editor': { minHeight: '400px' }
                        }}>
                            <ReactQuill
                                theme="snow"
                                value={conteudo}
                                onChange={setConteudo}
                                modules={modules}
                                formats={formats}
                            />
                        </Box>
                    ) : (
                        <TextField
                            multiline
                            rows={20}
                            fullWidth
                            variant="outlined"
                            value={conteudo}
                            onChange={(e) => setConteudo(e.target.value)}
                            placeholder="Edite o código HTML aqui..."
                            InputProps={{
                                sx: { fontFamily: 'monospace', fontSize: '14px' }
                            }}
                        />
                    )}
                </Box>

                {/* Documentation of Variables */}
                <Box sx={{ mb: 3, p: 2, backgroundColor: '#f5f5f5', borderRadius: 1, border: '1px solid #e0e0e0' }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1, color: '#333' }}>
                        Variáveis Disponíveis para Uso:
                    </Typography>

                    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))', gap: 1 }}>
                        {/* Identificação */}
                        <Typography variant="caption" sx={{ color: '#555' }}><strong>&#123;&#123;nome&#125;&#125;</strong>: Nome / Razão Social</Typography>
                        <Typography variant="caption" sx={{ color: '#555' }}><strong>&#123;&#123;codigo_credenciado&#125;&#125;</strong>: Código do Credenciado (ID+Data)</Typography>
                        <Typography variant="caption" sx={{ color: '#555' }}><strong>&#123;&#123;nome_fantasia&#125;&#125;</strong>: Nome Fantasia (PJ)</Typography>
                        <Typography variant="caption" sx={{ color: '#555' }}><strong>&#123;&#123;cpf&#125;&#125;</strong>: CPF</Typography>
                        <Typography variant="caption" sx={{ color: '#555' }}><strong>&#123;&#123;cnpj&#125;&#125;</strong>: CNPJ</Typography>
                        <Typography variant="caption" sx={{ color: '#555' }}><strong>&#123;&#123;rg_ie&#125;&#125;</strong>: RG ou Inscrição Estadual</Typography>
                        <Typography variant="caption" sx={{ color: '#555' }}><strong>&#123;&#123;nacionalidade&#125;&#125;</strong>: Nacionalidade</Typography>
                        <Typography variant="caption" sx={{ color: '#555' }}><strong>&#123;&#123;estado_civil&#125;&#125;</strong>: Estado Civil</Typography>
                        <Typography variant="caption" sx={{ color: '#555' }}><strong>&#123;&#123;dt_nascimento_abertura&#125;&#125;</strong>: Data Nasc. / Abertura</Typography>

                        {/* Endereço */}
                        <Typography variant="caption" sx={{ color: '#555' }}><strong>&#123;&#123;endereco&#125;&#125;</strong>: Endereço Completo</Typography>
                        <Typography variant="caption" sx={{ color: '#555' }}><strong>&#123;&#123;cep&#125;&#125;</strong>: CEP</Typography>
                        <Typography variant="caption" sx={{ color: '#555' }}><strong>&#123;&#123;cidade&#125;&#125;</strong>: Cidade</Typography>
                        <Typography variant="caption" sx={{ color: '#555' }}><strong>&#123;&#123;estado&#125;&#125;</strong>: Estado (UF)</Typography>

                        {/* Contato */}
                        <Typography variant="caption" sx={{ color: '#555' }}><strong>&#123;&#123;email&#125;&#125;</strong>: E-mail</Typography>
                        <Typography variant="caption" sx={{ color: '#555' }}><strong>&#123;&#123;telefone&#125;&#125;</strong>: Telefone</Typography>
                        <Typography variant="caption" sx={{ color: '#555' }}><strong>&#123;&#123;whatsapp&#125;&#125;</strong>: WhatsApp</Typography>

                        {/* Bancários & Comercial */}
                        <Typography variant="caption" sx={{ color: '#555' }}><strong>&#123;&#123;banco&#125;&#125;</strong>: Banco</Typography>
                        <Typography variant="caption" sx={{ color: '#555' }}><strong>&#123;&#123;agencia&#125;&#125;</strong>: Agência</Typography>
                        <Typography variant="caption" sx={{ color: '#555' }}><strong>&#123;&#123;conta&#125;&#125;</strong>: Conta Corrente</Typography>
                        <Typography variant="caption" sx={{ color: '#555' }}><strong>&#123;&#123;pix&#125;&#125;</strong>: Chave PIX</Typography>
                        <Typography variant="caption" sx={{ color: '#555' }}><strong>&#123;&#123;comissao&#125;&#125;</strong>: % Comissão</Typography>
                        <Typography variant="caption" sx={{ color: '#555' }}><strong>&#123;&#123;regiao&#125;&#125;</strong>: Região de Atuação</Typography>

                        {/* Unidades & Outros */}
                        <Typography variant="caption" sx={{ color: '#555' }}><strong>&#123;&#123;unidade X&#125;&#125;</strong>: Dados da Unidade (ex: &#123;&#123;unidade 1&#125;&#125;)</Typography>
                        <Typography variant="caption" sx={{ color: '#555' }}><strong>&#123;&#123;data_atual&#125;&#125;</strong>: Data de Emissão</Typography>
                    </Box>
                </Box>

                <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <Button
                        variant="contained"
                        color="primary"
                        startIcon={loading ? <Preloader size={20} color="inherit" /> : <Save />}
                        onClick={handleSalvar}
                        disabled={loading || !selectedModeloId}
                        sx={{ bgcolor: '#20ac9c', '&:hover': { bgcolor: '#1a8a7d' } }}
                    >
                        {loading ? 'Salvando...' : 'Salvar Alterações'}
                    </Button>
                </Box>
            </Paper>
        </Container>
    );
}
